import { createFileRoute, Link } from "@tanstack/react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Store, Flag, Users, Clock, CheckCircle2, AlertTriangle, MapPin } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { kindLabel, statusLabel } from "@/lib/admin-labels";

export const Route = createFileRoute("/admin/")({
  component: AdminDashboard,
});

function AdminDashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "dashboard"],
    queryFn: async () => {
      const [pending, approved, openReports, users, mapOptIn, mapVisible] = await Promise.all([
        supabase.from("businesses").select("id,name,kind,city,country,submitted_at,status", { count: "exact" }).eq("status", "pending").order("submitted_at", { ascending: false }).limit(5),
        supabase.from("businesses").select("id", { count: "exact", head: true }).eq("status", "approved"),
        supabase.from("reports").select("id,business_id,reason,created_at,status,businesses(name)").eq("status", "open").order("created_at", { ascending: false }).limit(5),
        supabase.from("profiles").select("id", { count: "exact", head: true }),
        supabase.from("profiles").select("id", { count: "exact", head: true }).eq("show_on_map", true),
        supabase.from("profiles").select("id", { count: "exact", head: true }).eq("show_on_map", true).not("approx_latitude", "is", null).not("approx_longitude", "is", null),
      ]);
      const optIn = mapOptIn.count ?? 0;
      const visible = mapVisible.count ?? 0;
      return {
        pendingList: pending.data ?? [],
        pendingCount: pending.count ?? 0,
        approvedCount: approved.count ?? 0,
        openReportsList: openReports.data ?? [],
        openReportsCount: openReports.data?.length ?? 0,
        usersCount: users.count ?? 0,
        mapOptInCount: optIn,
        mapVisibleCount: visible,
        mapMissingCount: Math.max(optIn - visible, 0),
      };
    },
  });

  const stats: { label: string; value: number; icon: typeof Clock; to: string; search?: Record<string, string> }[] = [
    { label: "Čeká na schválení", value: data?.pendingCount ?? 0, icon: Clock, to: "/admin/podniky", search: { status: "pending" } },
    { label: "Schválené podniky", value: data?.approvedCount ?? 0, icon: CheckCircle2, to: "/admin/podniky", search: { status: "approved" } },
    { label: "Otevřené reporty", value: data?.openReportsCount ?? 0, icon: AlertTriangle, to: "/admin/reporty" },
    { label: "Registrovaní uživatelé", value: data?.usersCount ?? 0, icon: Users, to: "/admin/uzivatele" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-3xl">Přehled moderace</h1>
        <p className="text-sm text-muted-foreground">Co se děje v katalogu Cerveza.International právě teď. Klikněte na kartu pro detailní seznam.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((s) => (
          <Link
            key={s.label}
            to={s.to}
            search={s.search as never}
            className="block transition-transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-lg"
          >
            <Card className="cursor-pointer transition-shadow hover:shadow-md">
              <CardContent className="flex items-center justify-between p-5">
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">{s.label}</div>
                  <div className="mt-1 font-serif text-3xl">{isLoading ? "—" : s.value}</div>
                </div>
                <s.icon className="h-8 w-8 text-muted-foreground" />
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-serif text-xl">
            <MapPin className="h-5 w-5" /> Členové Direct Clubu na mapě
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="rounded-md border p-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Souhlasí se zobrazením</div>
              <div className="mt-1 font-serif text-2xl">{isLoading ? "—" : data?.mapOptInCount ?? 0}</div>
              <div className="mt-1 text-xs text-muted-foreground">profily s <code>show_on_map = true</code></div>
            </div>
            <div className="rounded-md border p-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Zobrazeni jako pin</div>
              <div className="mt-1 font-serif text-2xl text-emerald-600">{isLoading ? "—" : data?.mapVisibleCount ?? 0}</div>
              <div className="mt-1 text-xs text-muted-foreground">mají vyplněnou přibližnou polohu</div>
            </div>
            <div className="rounded-md border p-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Chybí poloha</div>
              <div className={`mt-1 font-serif text-2xl ${(data?.mapMissingCount ?? 0) > 0 ? "text-amber-600" : ""}`}>
                {isLoading ? "—" : data?.mapMissingCount ?? 0}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                <code>approx_latitude</code>/<code>approx_longitude</code> je NULL — poloha se doplní při dalším přihlášení uživatele (z Cloudflare geo hlaviček v produkci)
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2 font-serif text-xl">
              <Store className="h-5 w-5" /> Nové podniky k posouzení
            </CardTitle>
            <Button asChild size="sm" variant="outline"><Link to="/admin/podniky">Vše</Link></Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {(data?.pendingList ?? []).length === 0 && (
              <p className="text-sm text-muted-foreground">Nic nečeká na schválení. 🎉</p>
            )}
            {(data?.pendingList ?? []).map((b) => (
              <div key={b.id} className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <div className="font-medium">{b.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {kindLabel[b.kind as keyof typeof kindLabel]} · {b.city}, {b.country} · {new Date(b.submitted_at).toLocaleDateString("cs-CZ")}
                  </div>
                </div>
                <Badge variant="outline">{statusLabel[b.status as keyof typeof statusLabel]}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2 font-serif text-xl">
              <Flag className="h-5 w-5" /> Nejnovější reporty
            </CardTitle>
            <Button asChild size="sm" variant="outline"><Link to="/admin/reporty">Vše</Link></Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {(data?.openReportsList ?? []).length === 0 && (
              <p className="text-sm text-muted-foreground">Žádné otevřené reporty.</p>
            )}
            {(data?.openReportsList ?? []).map((r) => (
              <div key={r.id} className="flex items-start justify-between rounded-md border p-3">
                <div>
                  <div className="font-medium">{(r.businesses as { name: string } | null)?.name ?? "—"}</div>
                  <div className="text-xs text-muted-foreground">
                    {r.reason} · {new Date(r.created_at).toLocaleDateString("cs-CZ")}
                  </div>
                </div>
                <Badge variant="destructive">otevřený</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
